import React, {
  createContext, useContext, useRef, useState, useCallback, useMemo, useEffect,
} from 'react';
import { IS_CAPACITOR } from './platform';

/*
 * NavHistory — global in-app navigation history (Task #65).
 *
 * There is no router: navigation is `activePage` state in <AuthenticatedApp/>
 * switched via setActivePage (passed everywhere as `onNavigate`). This module
 * is the single history stack behind the global Back control.
 *
 * Phase 1 (shipped): route-level Back.
 * Phase A (shipped): UX upgrade — object stack entries { page, label, ts },
 *   15-entry cap, sessionStorage refresh-survival, disabled (not hidden) empty
 *   state, keyboard shortcuts, and a jump-to-entry history dropdown.
 *   Labels are GENERIC page titles; never PHI.
 * Phase B1 (shipped): useNavStep — state-driven sub-views register as one Back
 *   step (adopted by PermissionManager / AccessControlHub).
 * Phase B2 (this change): EDGE CASES —
 *   - dirty-form guard: useNavGuard({ when, message }) registers a predicate the
 *     controller consults before ANY navigation away (Back button, keyboard,
 *     hardware back, sidebar). Confirmed once per action; decline = stay put.
 *   - shell parity: the Capacitor Android hardware Back button is wired to the
 *     SAME back()/guard path (no history left → App.exitApp(), Android
 *     convention). Electron already inherits parity from the shared on-screen
 *     button + keyboard shortcuts (same bundle in the webview); an optional
 *     native menu item can later route through window.zignagenixAPI.onNavBack.
 *   Still no per-view state persisted; guard predicates are booleans, never PHI.
 *
 * Invariants preserved from Phase 1:
 *   - navigate(next) is the SINGLE push choke point (push current, then switch).
 *   - Auth/landing transitions call the RAW setActivePage (never navigate) → no
 *     history entry → post-login landing preserved, Back disabled on first load.
 *   - back()/jumpTo() restore via the RAW setter behind a `backing` guard, so
 *     they can never re-push.
 *   - De-dupe: navigating to the current page is a no-op.
 *
 * State manager: the existing custom NavHistoryContext (no Zustand/Redux in the
 * project — none added). All hooks are top-level/unconditional (repo-wide
 * react-hooks/rules-of-hooks gate, zero exemptions).
 */

const MAX_ENTRIES = 15;                 // cap; oldest dropped beyond this
const STORE_PREFIX = 'zg.nav.v1.';      // sessionStorage key (per session id)

// ── Generic page titles (NEVER PHI — page-level only) ───────────────────────
const PAGE_LABELS = {
  dashboard: 'Dashboard', patients: 'Patients', encounters: 'Encounters',
  labs: 'Labs', medications: 'Medications', vitals: 'Vitals', allergies: 'Allergies',
  messages: 'Messages', chat: 'Messages', email: 'Messages',
  airesearch: 'AI Research', bi: 'Business Intelligence',
  revenue: 'Revenue', billing: 'Revenue', stripe: 'Revenue',
  stripereports: 'Revenue', estimates: 'Revenue',
  scheduling: 'Scheduling', crm: 'CRM', 'my-day': 'CRM', adminai: 'Admin AI',
  activity: 'Activity', notifications: 'Activity', aialerts: 'Activity',
  dataexport: 'Data Export', portal: 'Patient Portal', patientportal: 'Patient Portal',
  totp: 'Security Setup', audit: 'Audit Log', settings: 'Settings',
  awscloud: 'AWS Cloud', gohighlevel: 'GoHighLevel',
  research: 'Research', researchdb: 'Research', biolab: 'Research',
  reports: 'Reports', personnel: 'Personnel', leave: 'Personnel',
  personnelactions: 'Personnel', telehealth: 'Telehealth',
};
export function labelForPage(page) {
  if (PAGE_LABELS[page]) return PAGE_LABELS[page];
  // Fallback: title-case an unknown key (still generic — it's a route key).
  return String(page || '')
    .split(/[-_\s]+/).filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ') || 'Page';
}

// ── sessionStorage helpers (page history only; never per-view state) ────────
function storeKey(sessionKey) { return STORE_PREFIX + sessionKey; }
function readStored(sessionKey) {
  try {
    const raw = sessionStorage.getItem(storeKey(sessionKey));
    if (!raw) return null;
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return null;
    // Validate shape defensively; drop anything that isn't {page,label,ts}.
    return arr.filter((e) => e && typeof e.page === 'string').slice(-MAX_ENTRIES);
  } catch { return null; }
}
function writeStored(sessionKey, entries) {
  try { sessionStorage.setItem(storeKey(sessionKey), JSON.stringify(entries)); } catch { /* quota/unavailable */ }
}
function clearStored(sessionKey) {
  try { sessionStorage.removeItem(storeKey(sessionKey)); } catch { /* noop */ }
}

export const NavHistoryContext = createContext(null);

// Owns the history stack. Called ONCE at the top of <AuthenticatedApp/>.
export function useNavHistoryController(page, setPage, sessionKey = null) {
  const [stack, setStack] = useState([]);   // { page, label, ts }[], oldest → newest
  const pageRef = useRef(page);
  const stackRef = useRef(stack);
  const backing = useRef(false);
  const prevKeyRef = useRef(null);
  const stepIdRef = useRef(0);
  const guardsRef = useRef(new Map());   // id -> { whenRef, messageRef } (dirty-form guards)
  const guardSeq = useRef(0);
  pageRef.current = page;
  stackRef.current = stack;

  // Dirty-form guard registry. useNavGuard() registers a live predicate; the
  // navigation paths below (navigate / back / jumpTo — and therefore the Back
  // button, keyboard shortcuts, and the Capacitor hardware button) consult it
  // before leaving. If any guard is active, confirm ONCE; decline = abort the
  // navigation and stay put. Predicates are booleans + generic messages, never
  // PHI. Registration returns an unregister fn (called on unmount).
  const registerGuard = useCallback((entry) => {
    const id = ++guardSeq.current;
    guardsRef.current.set(id, entry);
    return () => { guardsRef.current.delete(id); };
  }, []);
  // P110 (d): the idle-warning dialog needs to know whether unsaved work is on
  // screen so it can SAY so before the automatic logoff discards it. It reads
  // the same registry the navigation guard uses, so there is one definition of
  // "dirty" rather than a second one that could disagree.
  //
  // Returns a COUNT, never the messages and never field contents — the dialog
  // must not become a new place PHI is rendered. Nothing here auto-saves.
  const unsavedWorkCount = useCallback(
    () => [...guardsRef.current.values()].filter((g) => g.whenRef.current).length,
    []
  );
  const mayLeave = useCallback(() => {
    // Single prompt per navigation action BY DESIGN, even when several guards
    // are dirty at once (Phase C wires many consumers — a dirty note with a
    // dirty sub-form/modal open must not stack prompts). Wording is
    // COUNT-AWARE: with exactly one dirty guard we use its specific message;
    // with MORE THAN ONE we use a generic message, because showing one form's
    // "leave without saving this note?" while a second form is also dirty
    // would let the user confirm and unknowingly discard the second (review
    // flag, #278). Semantics unchanged — still ONE confirm. No active guard ->
    // allow. No window.confirm (SSR/test) -> allow. Guards carry only a
    // boolean + a generic string, never PHI.
    const dirty = [...guardsRef.current.values()].filter((g) => g.whenRef.current);
    if (dirty.length === 0) return true;
    const msg = dirty.length > 1
      ? 'You have unsaved changes in more than one place. Leave without saving?'
      : (dirty[0].messageRef.current || 'You have unsaved changes. Leave this page?');
    // eslint-disable-next-line no-alert
    return (typeof window !== 'undefined' && typeof window.confirm === 'function')
      ? window.confirm(msg) : true;
  }, []);

  // Session reconcile + refresh-survival, keyed by session id:
  //   - login / refresh-restore (id known): restore that id's stored history
  //     (survives F5 within the tab).
  //   - logout (had id, now null): clear in-memory AND that id's stored key.
  //   - loading null on first mount: no-op (don't wipe a refresh's stored data).
  // sessionStorage only (dies on tab close); never localStorage, never backend.
  useEffect(() => {
    const prev = prevKeyRef.current;
    prevKeyRef.current = sessionKey;
    if (sessionKey == null) {
      if (prev != null) clearStored(prev);   // real logout
      setStack([]);
      return;
    }
    if (prev != null && prev !== sessionKey) { clearStored(prev); setStack([]); return; }
    setStack(readStored(sessionKey) || []);  // restore (refresh / re-entry)
  }, [sessionKey]);

  // Persist ROUTE history only ({page,label,ts}) on every change. Step entries
  // (in-memory sub-view state) are NEVER persisted — their restore() is a live
  // closure and their context could carry PHI-adjacent view state (Phase B).
  useEffect(() => {
    if (sessionKey == null) return;
    writeStored(sessionKey, stack.filter((e) => e.kind !== 'step'));
  }, [stack, sessionKey]);

  const pushEntry = (entry) => setStack((s) => {
    const ns = [...s, entry];
    return ns.length > MAX_ENTRIES ? ns.slice(-MAX_ENTRIES) : ns;
  });

  const navigate = useCallback((next) => {
    if (next == null || next === pageRef.current) return;   // ignore + de-dupe
    if (!backing.current && !mayLeave()) return;            // dirty-form guard
    if (!backing.current) {
      pushEntry({ kind: 'route', page: pageRef.current, label: labelForPage(pageRef.current), ts: Date.now() });
    }
    setPage(next);
  }, [setPage, mayLeave]);

  // Push a state-driven sub-view STEP (Phase B). restore() reverses it (the
  // component's onBack). Returns an id so the component can pop its own entry.
  // In-memory only; never persisted.
  const pushStep = useCallback(({ label, restore }) => {
    if (backing.current) return null;             // don't push while unwinding
    const id = ++stepIdRef.current;
    pushEntry({ kind: 'step', id, label, ts: Date.now(), restore });
    return id;
  }, []);

  // Remove a step entry by id from anywhere in the stack. Idempotent (no-op if
  // already gone — e.g. Back popped it first), so there is never a double-pop.
  const popStep = useCallback((id) => {
    setStack((s) => (s.some((e) => e.kind === 'step' && e.id === id)
      ? s.filter((e) => !(e.kind === 'step' && e.id === id)) : s));
  }, []);

  // Unify Back + jump: unwind the stack down to targetLen, invoking each popped
  // entry's reversal top-down (most-recent first) under the `backing` guard so
  // the resulting state changes (onBack -> active=false, setPage) never re-push.
  // route -> setPage(page); step -> restore() (onBack). Batched: the last
  // reversal invoked (the target) wins the landing page.
  const unwindTo = useCallback((targetLen) => {
    const s = stackRef.current;
    if (targetLen < 0 || targetLen >= s.length) return;
    backing.current = true;
    try {
      for (let i = s.length - 1; i >= targetLen; i--) {
        const e = s[i];
        if (e.kind === 'step') { try { e.restore && e.restore(); } catch (_) { /* best-effort */ } }
        else setPage(e.page);
      }
    } finally {
      setStack(s.slice(0, targetLen));
      backing.current = false;
    }
  }, [setPage]);

  // Back = unwind exactly one level (route or step). Consults the dirty-form
  // guard first (this is the single path the Back button, keyboard shortcuts,
  // and the Capacitor hardware button all call).
  const back = useCallback(() => {
    if (!mayLeave()) return;
    unwindTo(stackRef.current.length - 1);
  }, [unwindTo, mayLeave]);

  // Jump to a stack index (0 = oldest): unwind through that entry + everything
  // newer, landing at the selected point. Guarded like back().
  const jumpTo = useCallback((index) => {
    if (!mayLeave()) return;
    unwindTo(index);
  }, [unwindTo, mayLeave]);

  // Keyboard shortcuts (attached in the provider): Alt+← and Ctrl/Cmd+[ go
  // Back. Ignored while typing (input/textarea/select/contenteditable) so "["
  // isn't hijacked. Cleaned up on unmount.
  useEffect(() => {
    const isTyping = () => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable === true;
    };
    const onKey = (e) => {
      if (isTyping()) return;
      const altLeft = e.altKey && e.key === 'ArrowLeft';
      const modBracket = (e.ctrlKey || e.metaKey) && (e.key === '[' || e.code === 'BracketLeft');
      if (altLeft || modBracket) { e.preventDefault(); back(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [back]);

  // Capacitor Android hardware Back button (shell parity). Wired to the SAME
  // back() path (which runs the dirty-form guard) so there is one navigation
  // model everywhere. When there is nowhere left to go back to, defer to the
  // Android convention: exit the app rather than trap the user. Dynamically
  // imported so the web/electron bundles never pull the native plugin, and
  // gated on IS_CAPACITOR so this effect is inert off-device.
  useEffect(() => {
    if (!IS_CAPACITOR) return undefined;
    let handle = null;
    let cancelled = false;
    import('@capacitor/app')
      .then(({ App }) => {
        if (cancelled) return;
        App.addListener('backButton', () => {
          if (stackRef.current.length > 0) back();   // back() guards internally
          else App.exitApp();
        }).then((h) => { handle = h; });
      })
      .catch(() => { /* plugin unavailable — on-screen Back still works */ });
    return () => { cancelled = true; if (handle && handle.remove) handle.remove(); };
  }, [back]);

  const canGoBack = stack.length > 0;
  /*
   * ⛔⛔ `mayLeave` IS EXPOSED — Card PIPELINE-PLACE-AND-FOCUS, review round 5.
   *
   * The dirty-form guard was reachable only from navigate/back/jumpTo, which
   * was right while those were the only ways to leave a view. The browser's
   * own Back button is now wired (src/lib/browserBackStep.js) and its
   * traversal is SAME-DOCUMENT, so the `beforeunload` that used to catch a
   * half-written note never fires. Both of the new paths — the hook's close
   * and App's page adoption on popstate — must ask the same question every
   * other control asks, and this is that question.
   */
  return useMemo(
    () => ({ navigate, back, jumpTo, pushStep, popStep, registerGuard, mayLeave, unsavedWorkCount, canGoBack, history: stack, depth: stack.length }),
    [navigate, back, jumpTo, pushStep, popStep, registerGuard, mayLeave, unsavedWorkCount, canGoBack, stack]
  );
}

// Full controller (Back button + dropdown). Safe no-op defaults outside provider.
export function useNavHistory() {
  return useContext(NavHistoryContext) || {
    navigate: () => {}, back: () => {}, jumpTo: () => {},
    pushStep: () => null, popStep: () => {}, registerGuard: () => () => {},
    // ⚠ Outside a provider there is nothing to guard, so leaving is allowed.
    mayLeave: () => true,
    // ⚠ ...and nothing is registered, so there is no unsaved work to report.
    unsavedWorkCount: () => 0,
    canGoBack: false, history: [], depth: 0,
  };
}
// Back-only convenience (kept from Phase 1).
export function useBack() {
  const { back, canGoBack } = useNavHistory();
  return { back, canGoBack };
}

/*
 * useNavGuard — prompt before navigating away from unsaved work (Phase B2).
 *
 * Call UNCONDITIONALLY at the top of a form component (rules-of-hooks safe).
 * `when` is a boolean: "there are unsaved changes right now." While it is true,
 * ANY navigation away — the Back button, Alt+<- / Ctrl-Cmd+[, the Capacitor
 * hardware back, jump-to-history, or a sidebar navigate() — first shows a
 * confirm(message); declining aborts the navigation and leaves the user on the
 * form. `when` and `message` are read through refs so the live predicate is
 * always current without re-registering.
 *
 * PRIVACY: `message` must be GENERIC (e.g. "You have unsaved changes to this
 * note.") — never a patient name or identifier. The guard registers only a
 * boolean + a static string; no view state, no PHI, leaves the model.
 */
export function useNavGuard({ when, message }) {
  const { registerGuard } = useNavHistory();
  const whenRef = useRef(when);
  const messageRef = useRef(message);
  whenRef.current = when;
  messageRef.current = message;
  useEffect(() => {
    if (!registerGuard) return undefined;
    return registerGuard({ whenRef, messageRef });
  }, [registerGuard]);
}

/*
 * useNavStep — register a state-driven sub-view as ONE Back step (Phase B).
 *
 * Call UNCONDITIONALLY at the top of a component (rules-of-hooks safe). `active`
 * is a boolean: "this sub-view is currently open." When it flips false->true a
 * history step is pushed whose reversal calls onBack() (which should set active
 * back to false); when it flips true->false, or the component unmounts, that
 * step is popped if still present. The global Back button, Alt+<- / Ctrl-Cmd+[,
 * and the dropdown all reverse it — one level per click, correctly interleaved
 * with (and nested above) route-level entries.
 *
 * `label` must be GENERIC (e.g. 'User detail') — NEVER PHI / names. onBack and
 * label are read through refs so the pushed reversal always uses the latest
 * closure without re-pushing.
 */
export function useNavStep({ active, label, onBack }) {
  const { pushStep, popStep } = useNavHistory();
  const idRef = useRef(null);
  const onBackRef = useRef(onBack);
  const labelRef = useRef(label);
  onBackRef.current = onBack;
  labelRef.current = label;

  useEffect(() => {
    if (active) {
      if (idRef.current == null) {
        idRef.current = pushStep({
          label: labelRef.current,
          restore: () => { if (onBackRef.current) onBackRef.current(); },
        });
      }
    } else if (idRef.current != null) {
      popStep(idRef.current);
      idRef.current = null;
    }
  }, [active, pushStep, popStep]);

  // Unmount safety net: if we unmount while still active (e.g. a route change
  // tore the whole page down), clean our entry so it can't linger in the stack.
  useEffect(() => () => {
    if (idRef.current != null) { popStep(idRef.current); idRef.current = null; }
  }, [popStep]);
}

function fmtTime(ts) {
  try { return new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }); }
  catch { return ''; }
}

// The single global Back control: ← Back + a caret that opens a jump-to
// history dropdown. Long-press on Back also opens it. Always rendered; disabled
// (greyed) — not hidden — when there is nowhere to go back to.
export function NavBackButton() {
  const { back, jumpTo, canGoBack, history } = useNavHistory();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const longPress = useRef(null);

  // Close the dropdown on outside click / Escape (only while open).
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const startLongPress = useCallback(() => {
    if (!canGoBack) return;
    longPress.current = setTimeout(() => setOpen(true), 450);
  }, [canGoBack]);
  const cancelLongPress = useCallback(() => {
    if (longPress.current) { clearTimeout(longPress.current); longPress.current = null; }
  }, []);

  // Dropdown lists most-recent first; map display index → stack index.
  const items = useMemo(
    () => history.map((e, i) => ({ ...e, stackIndex: i })).reverse(),
    [history]
  );

  return (
    <div className="app-back-header" ref={rootRef}>
      <div className="app-back-group" role="group" aria-label="Navigation history">
        <button
          type="button"
          className="app-back-btn"
          onClick={back}
          disabled={!canGoBack}
          aria-disabled={!canGoBack}
          aria-label="Back"
          title="Back to previous page"
          onMouseDown={startLongPress}
          onMouseUp={cancelLongPress}
          onMouseLeave={cancelLongPress}
          onTouchStart={startLongPress}
          onTouchEnd={cancelLongPress}
        >
          <span className="app-back-arrow" aria-hidden="true">←</span>
          <span className="app-back-text">Back</span>
        </button>
        <button
          type="button"
          className="app-back-caret"
          onClick={() => canGoBack && setOpen((v) => !v)}
          disabled={!canGoBack}
          aria-disabled={!canGoBack}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Recent history"
          title="Recent history"
        >
          <span aria-hidden="true">▾</span>
        </button>
      </div>

      {open && canGoBack && (
        <div className="app-back-menu" role="menu" aria-label="Recent pages">
          {items.map((it) => (
            <button
              key={`${it.stackIndex}-${it.ts}`}
              type="button"
              role="menuitem"
              className="app-back-menu-item"
              onClick={() => { jumpTo(it.stackIndex); setOpen(false); }}
            >
              <span className="app-back-menu-label">{it.label}</span>
              <span className="app-back-menu-time">{fmtTime(it.ts)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
